import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera, Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface AvatarUploadProps {
  currentAvatarUrl?: string | null;
  username: string;
  userId: string;
  onAvatarUpdate: (avatarUrl: string | null) => void;
  size?: 'sm' | 'md' | 'lg';
}

const AvatarUpload = ({ 
  currentAvatarUrl, 
  username, 
  userId, 
  onAvatarUpdate,
  size = 'lg'
}: AvatarUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine valido');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Il file è troppo grande. Massimo 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    await uploadAvatar(file);
  };

  const uploadAvatar = async (file: File) => {
    setUploading(true);
    try {
      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('profile-avatars')
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: data.publicUrl })
        .eq('user_id', userId);

      if (updateError) throw updateError;

      onAvatarUpdate(data.publicUrl);
      setPreviewUrl(null);
      toast.success('Foto profilo aggiornata!');
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Errore nel caricamento della foto');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const removeAvatar = async () => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('user_id', userId);

      if (error) throw error;

      onAvatarUpdate(null);
      setPreviewUrl(null);
      toast.success('Foto profilo rimossa!');
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast.error('Errore nella rimozione della foto');
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Avatar Display */}
      <div className="relative group">
        <Avatar className={`${sizeClasses[size]} ring-4 ring-primary/30`}>
          <AvatarImage src={previewUrl || currentAvatarUrl || undefined} />
          <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-bold">
            {username.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        
        {/* Upload Overlay */}
        <Label
          htmlFor={`avatar-upload-${userId}`}
          className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          ) : (
            <Camera className="w-6 h-6 text-white" />
          )}
        </Label>
      </div>

      {/* Upload Controls */}
      <div className="flex gap-2">
        <Label htmlFor={`avatar-upload-${userId}`} className="cursor-pointer">
          <Button disabled={uploading} variant="outline" size="sm" asChild>
            <span>
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Caricamento...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Carica
                </>
              )}
            </span>
          </Button>
        </Label>

        {(currentAvatarUrl || previewUrl) && (
          <Button
            variant="outline"
            size="sm"
            onClick={removeAvatar}
            className="border-red-500/20 text-red-400 hover:bg-red-500/10"
          >
            <X className="w-4 h-4 mr-2" />
            Rimuovi
          </Button>
        )}
      </div>

      {/* Hidden File Input */}
      <Input
        id={`avatar-upload-${userId}`}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />

      {size === 'lg' && (
        <p className="text-xs text-muted-foreground text-center">
          JPG, PNG o GIF. Massimo 5MB.
        </p>
      )}
    </div>
  );
};

export default AvatarUpload;